<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-primary" @click="handleCreate">
        <i class="ri-add-line"></i>
        新增帳號
      </button>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋登入帳號、Email、姓名或客戶名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-box" v-if="isSystemAccount">
        <label>平台篩選：</label>
        <select v-model="selectedPlatformId" @change="handleFilterChange" class="form-control">
          <option value="">全部平台</option>
          <option
            v-for="platform in platforms"
            :key="platform.id"
            :value="platform.id"
          >
            {{ platform.name }} ({{ platform.code }})
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchAccounts">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>登入帳號</th>
            <th>Email</th>
            <th>姓名</th>
            <th>客戶</th>
            <th>角色</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="accounts.length === 0">
            <td colspan="8" class="empty-state">
              沒有找到帳號
            </td>
          </tr>
          <tr v-for="account in accounts" :key="account.id">
            <td>{{ account.username }}</td>
            <td>{{ account.email }}</td>
            <td>{{ account.name || '-' }}</td>
            <td>
              <span v-if="account.clientName" class="badge badge-info">
                {{ account.clientName }}
              </span>
              <span v-else class="badge badge-secondary">-</span>
            </td>
            <td>
              <span class="badge badge-info">{{ getAccountRoleLabel(account.role, account.roleName) }}</span>
            </td>
            <td>
              <span
                :class="account.status === 'active' ? 'badge badge-success' : 'badge badge-danger'"
              >
                {{ account.status === 'active' ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatDate(account.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(account)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(account)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { clientAccountService } from '@/api/modules/clientAccounts.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import authService from '@/helpers/authservice/authService.js';
import { getAccountRoleLabel } from '@/utils/accountRoleLabel.js';

export default {
  name: 'ClientAccounts',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('客戶帳號列表');
    const accounts = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const searchTimeout = ref(null);
    const selectedPlatformId = ref('');
    const platforms = ref([]);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const isSystemAccount = computed(() => {
      return authService.getAccountType() === 'system';
    });

    const fetchPlatforms = async () => {
      if (!isSystemAccount.value) return;
      
      try {
        const response = await platformManagementService.getPlatforms({ page: 1, pageSize: 100 });
        if (response && response.success && response.data && response.data.items) {
          platforms.value = response.data.items.map(platform => ({
            id: platform.id,
            name: platform.platformName,
            code: platform.platformCode,
          }));
        }
      } catch (err) {
        console.error('獲取平台列表失敗:', err);
      }
    };

    const fetchAccounts = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        // 系統帳號可以選擇平台
        if (isSystemAccount.value && selectedPlatformId.value) {
          params.platformId = selectedPlatformId.value;
        }

        const response = await clientAccountService.getAccounts(params);

        if (response && response.success && response.data) {
          accounts.value = response.data.items || [];
          total.value = response.data.total || 0;
        } else {
          error.value = (response && response.message) || '獲取帳號列表失敗';
        }
      } catch (err) {
        console.error('獲取帳號列表失敗:', err);
        error.value = err.message || '獲取帳號列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchAccounts();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchAccounts();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchAccounts();
      }
    };

    const handleCreate = () => {
      router.push('/system/clients/accounts/create');
    };

    const handleEdit = (account) => {
      router.push(`/system/clients/accounts/${account.id}/edit`);
    };

    const handleDelete = async (account) => {
      // 確認對話框
      const confirmed = await showDeleteConfirm(account.username);
      
      if (!confirmed) {
        return;
      }

      // 顯示刪除中狀態
      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await clientAccountService.deleteAccount(account.id);

        if (response && response.success) {
          // 刪除成功，顯示成功訊息並刷新列表
          await showSuccess('刪除成功', `帳號「${account.username}」已成功刪除`);
          
          // 如果當前頁是最後一頁且只剩一筆資料，則回到上一頁
          if (accounts.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          // 刷新列表
          await fetchAccounts();
        } else {
          // 處理 API 返回的錯誤
          const errorMsg = (response && response.message) || '刪除帳號失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除帳號失敗:', err);
        
        // 處理特定錯誤
        let errorTitle = '刪除失敗';
        let errorText = '刪除帳號失敗，請稍後再試';
        
        if (err.code === 'CANNOT_DELETE_SELF') {
          errorText = '無法刪除自己的帳號';
        } else if (err.code === 'NOT_FOUND') {
          errorText = '帳號不存在或已被刪除';
        } else if (err.code === 'FORBIDDEN') {
          errorText = '您沒有權限刪除此帳號';
        } else if (err.message) {
          errorText = err.message;
        }
        
        await showError(errorTitle, errorText);
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    onMounted(() => {
      fetchPlatforms();
      fetchAccounts();
    });

    return {
      accounts,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      total,
      selectedPlatformId,
      platforms,
      isSystemAccount,
      pageTitle,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      formatDate,
      fetchAccounts,
      getAccountRoleLabel,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.page-toolbar {
  flex-wrap: wrap;
}

.table-wrapper table {
  min-width: 900px; /* 客戶帳號列表有 8 個欄位 */
}

/* 篩選器樣式 */
.filter-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.filter-box label {
  white-space: nowrap;
  font-size: 0.875rem;
  color: #666;
  min-width: fit-content;
}

.filter-box select {
  width: 200px;
  min-width: 150px;
  max-width: 250px;
}

/* 響應式設計 - 手機版 */
@media (max-width: 768px) {
  .page-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-box {
    flex: 1;
    max-width: 100%;
    width: 100%;
  }

  .filter-box {
    width: 100%;
  }

  .filter-box select {
    width: 100%;
  }
}
</style>
